'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink } from './primitives';

/**
 * The two places this product used to put the leak in front of the person it was
 * taken from: the address in blue, underlined, first in the cell, one click from
 * the page — and under it the pirate page's own title, printed verbatim, which is
 * routinely explicit and often contains her name. Neither is a bug; both are the
 * default, and the default is wrong here. The product already refuses to show her
 * a screenshot of these pages.
 */

/**
 * The pirate page's own title, behind a reveal.
 *
 * Hidden rather than removed, because it is sometimes the only thing that tells
 * two findings on the same host apart, and a person deciding what to file needs
 * it. The reveal is per row and does not persist: seeing it once was a choice
 * about that row, not a preference about the table.
 */
export function MaskedTitle({ title }: { title: string }) {
  const [shown, setShown] = useState(false);

  if (shown) {
    // No `is-shown` on the wrapper. It was carried for a rule that no
    // stylesheet has ever had: the revealed state is already told apart by the
    // inner span dropping `is-hidden`, and by the button reading Hide.
    return (
      <span className="masked-title">
        <span className="masked-title-text">{title}</span>
        <button type="button" className="masked-title-toggle" onClick={() => setShown(false)}>
          Hide
        </button>
      </span>
    );
  }

  return (
    <span className="masked-title">
      <span className="masked-title-text is-hidden">
        The page&rsquo;s own title
      </span>
      <span aria-hidden className="masked-title-sep">
        ·
      </span>
      <button
        type="button"
        className="masked-title-toggle"
        onClick={() => setShown(true)}
        title="Titles on these pages are usually explicit and often contain your name"
      >
        Show
      </button>
    </span>
  );
}

/**
 * The address, with a door rather than a doorway: clicking opens a panel saying
 * what it is and what opening it means, with two ways out that are not "look at
 * it". Opening it anyway stays one more click away, because sometimes she does
 * need to see it. The address is still shown in full — it identifies the page,
 * and hiding it would leave a table of indistinguishable rows.
 */
export function LeakAddress({
  url,
  display,
  className = '',
}: {
  url: string;
  /** The shortened form the table shows; the full address is still copied. */
  display?: string;
  className?: string;
}) {
  const [asking, setAsking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLSpanElement>(null);

  // Detections store the address without a scheme; a schemeless href is read as
  // a path on our own origin and opens nothing. Default to https so "Open it
  // anyway" (and the copied value) is the real external page.
  const href = /^[a-z][a-z0-9+.-]*:\/\//i.test(url) ? url : `https://${url}`;

  /*
    A floating panel, not a block that grows in place. A row is as tall as its
    tallest cell, so the warning pushed the whole row open under a 320px column
    while the other cells sat unchanged — the table lurching rather than a panel
    opening. It also sat inside .table-wrap's scroll box, which clips vertical
    overflow the moment overflow-x is not visible, and the two cannot be set
    apart. A portal to the body is the only way out; `position: fixed` at the
    trigger's coordinates is what makes it look anchored.
  */
  useEffect(() => {
    if (!asking || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // Clamp so the 360px panel never spills past the right edge when the trigger
    // sits in the narrow details drawer.
    const panelW = Math.min(360, window.innerWidth - 32);
    const left = Math.max(16, Math.min(rect.left, window.innerWidth - panelW - 16));
    setPos({ top: rect.bottom + 4, left });

    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setAsking(false);
    };
    // Closes rather than re-anchors: a tooltip that follows the scroll is
    // more moving parts than a warning shown once and dismissed needs.
    const closeOnScroll = () => setAsking(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', closeOnScroll, true);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [asking]);

  return (
    <span className="leakaddr">
      <button
        ref={triggerRef}
        type="button"
        className={`leakaddr-trigger ${className}`.trim()}
        aria-expanded={asking}
        onClick={() => setAsking((open) => !open)}
        title="Opens a warning first, not the page"
      >
        {display ?? url}
      </button>

      {asking && pos && typeof document !== 'undefined' && createPortal(
        <span
          ref={panelRef}
          className="leakaddr-warn"
          role="group"
          aria-label="Before you open this page"
          style={{ position: 'fixed', top: pos.top, left: pos.left }}
        >
          <span className="leakaddr-warn-lead">
            This opens the page itself, on the site hosting it.
          </span>
          <span className="leakaddr-warn-body">
            You do not have to look at it for us to act. Everything we can prove about this
            page is already on this screen, and the removal does not need you to have seen it.
          </span>
          <span className="leakaddr-warn-acts">
            <button
              type="button"
              className="btn btn-sm"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(href);
                  setCopied(true);
                } catch {
                  // Clipboard permission is the browser's to refuse. The
                  // address is on screen either way, so this is a convenience
                  // failing, not the feature.
                  setCopied(false);
                }
              }}
            >
              {copied ? 'Address copied' : 'Copy the address'}
            </button>
            <ExternalLink
              className="btn btn-sm leakaddr-open"
              href={url}
              onClick={() => setAsking(false)}
            >
              Open it anyway
            </ExternalLink>
            <button type="button" className="btn btn-sm" onClick={() => setAsking(false)}>
              Close
            </button>
          </span>
        </span>,
        // Into the modal's own backdrop when the trigger lives inside one (the
        // details drawer), so the panel stacks above the modal without a z-index
        // bump; the plain table has no such ancestor and falls back to the body.
        triggerRef.current?.closest('.modal-back') ?? document.body,
      )}
    </span>
  );
}
